import { useState } from "react";
import { Settings, ChevronRight, Sparkles, Brain } from "lucide-react";

// Radar dimensions
const radarCenter = { x: 90, y: 90 };
const radarRadius = 65;

const radarDimensions = [
  { label: "🌸 共情表达", value: 75, angle: -90 },
  { label: "🧠 逻辑表达", value: 55, angle: -30 },
  { label: "🌿 边界表达", value: 65, angle: 30 },
  { label: "🌊 描述能力", value: 70, angle: 90 },
  { label: "🎭 幽默表达", value: 40, angle: 150 },
  { label: "✨ 观察能力", value: 80, angle: 210 },
];

const growthTimeline = [
  { emoji: "🌱", title: "第一次主动表达自己的真实需求", date: "3天前" },
  { emoji: "🌸", title: "成功将一条收藏表达自然运用到排练中", date: "2天前" },
  { emoji: "🎭", title: "完成第一次人生复盘", date: "昨天" },
  { emoji: "🌊", title: "连续七天坚持表达练习", date: "今天" },
  { emoji: "📚", title: "收藏第100条表达", date: "上周" },
];

const echoInsights = [
  "过去一周，你越来越少使用「然后」「就是」等口头禅。",
  "你开始更多使用具体例子，而不是抽象评价。",
  "最近收藏的大多数表达都围绕「边界感」和「真诚沟通」，说明你正在关注如何更真实地表达自己。",
];

const expressionGarden = [
  { emoji: "🌸", type: "共情表达", count: 18, color: "#F9A8D4" },
  { emoji: "🌿", type: "真诚表达", count: 15, color: "#6EE7B7" },
  { emoji: "🌊", type: "描述表达", count: 12, color: "#93C5FD" },
  { emoji: "🎭", type: "幽默表达", count: 7, color: "#FCD34D" },
  { emoji: "⚔️", type: "边界表达", count: 10, color: "#C4B5FD" },
];

const weeklyQuote = {
  content: "真诚比完美更重要。",
  insight: "最近你越来越愿意表达真实，而不是努力表现正确。",
  source: "收藏于上周",
};

const echoLetter = [
  "这一周，你没有突然变得很会说话。",
  "但你开始敢说自己的感受了。",
  "你开始减少重复解释。",
  "开始学会拒绝。",
  "开始认真描述生活里的风景。",
  "",
  "这些改变不会立刻被别人发现。",
  "但未来某一天，",
  "它们都会变成你脱口而出的本能。",
  "",
  "保持现在的节奏。",
  "我们下周继续练习。",
];

export default function ProfilePage() {
  const [selectedFlower, setSelectedFlower] = useState<number | null>(null);

  // Calculate radar point position
  const getPoint = (value: number, angleDeg: number) => {
    const angleRad = (angleDeg * Math.PI) / 180;
    const r = (value / 100) * radarRadius;
    return {
      x: radarCenter.x + r * Math.cos(angleRad),
      y: radarCenter.y + r * Math.sin(angleRad),
    };
  };

  const radarPoints = radarDimensions
    .map((dim) => getPoint(dim.value, dim.angle))
    .map((p) => `${p.x},${p.y}`)
    .join(" ");

  return (
    <div className="relative min-h-full">
      {/* Background */}
      <div className="glow-orb" style={{ width: 260, height: 260, background: "#EAF4FF", top: -80, right: -100, opacity: 0.48 }} />
      <div className="glow-orb" style={{ width: 220, height: 220, background: "#DDF6E8", top: 290, left: -80, opacity: 0.4 }} />
      <div className="glow-orb" style={{ width: 200, height: 200, background: "#CDEAF8", top: 630, right: -70, opacity: 0.35 }} />
      <div className="glow-orb" style={{ width: 180, height: 180, background: "#DDF6E8", top: 460, left: 20, opacity: 0.3 }} />

      {/* Header */}
      <header className="relative px-5 pt-5 pb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-[28px]"
              style={{
                background: "linear-gradient(135deg, #EAF4FF, #DDF6E8)",
                boxShadow: "0 4px 16px rgba(234, 244, 255, 0.3)",
              }}
            >
              🌙
            </div>
            <div>
              <h2 className="text-[18px] font-bold text-echo-text">小回响</h2>
              <p className="text-[#6EE7B7] text-[12px] mt-0.5">🌱 正在学习认真表达的人</p>
              <p className="text-echo-text/35 text-[10px]">已经陪伴你 18 天</p>
            </div>
          </div>
          <button className="icon-btn">
            <Settings size={18} strokeWidth={2} className="text-echo-text/60" />
          </button>
        </div>

        <p className="text-echo-text/60 text-[13px] leading-relaxed italic">
          每一句认真说出口的话，都会成为未来的本能。
        </p>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-3 mt-4">
          {[
            { value: "18", label: "连续练习" },
            { value: "47", label: "累计练习" },
            { value: "23", label: "成功化用" },
          ].map((stat, i) => (
            <div key={i} className="glass-card rounded-2xl p-3 text-center">
              <p className="text-[20px] font-bold text-echo-text" style={{ fontFamily: '"SF Pro Display", system-ui' }}>
                {stat.value}
              </p>
              <p className="text-[11px] text-echo-text/40 mt-0.5">{stat.label}</p>
            </div>
          ))}
        </div>
      </header>

      {/* Expression Portrait */}
      <section className="px-5 pb-5">
        <h2 className="text-[17px] font-bold text-echo-text mb-3">🌱 我的表达画像</h2>
        <div className="glass-card rounded-card p-5">
          <div className="flex items-start gap-4">
            {/* Radar chart */}
            <div className="flex-shrink-0">
              <svg width="180" height="180" viewBox="0 0 180 180">
                {/* Grid hexagons */}
                {[0.2, 0.4, 0.6, 0.8, 1].map((scale, i) => (
                  <polygon
                    key={i}
                    points={radarDimensions
                      .map((dim) => {
                        const p = getPoint(scale * 100, dim.angle);
                        return `${p.x},${p.y}`;
                      })
                      .join(" ")}
                    fill="none"
                    stroke="#BDA9FF"
                    strokeOpacity="0.12"
                    strokeWidth="1"
                  />
                ))}

                {/* Axes */}
                {radarDimensions.map((dim, i) => {
                  const p = getPoint(100, dim.angle);
                  return (
                    <line
                      key={i}
                      x1={radarCenter.x}
                      y1={radarCenter.y}
                      x2={p.x}
                      y2={p.y}
                      stroke="#BDA9FF"
                      strokeOpacity="0.15"
                      strokeWidth="1"
                    />
                  );
                })}

                {/* Data fill */}
                <polygon
                  points={radarPoints}
                  fill="url(#radarGradient)"
                  fillOpacity="0.35"
                  stroke="#BDA9FF"
                  strokeWidth="1.5"
                  filter="url(#radarGlow)"
                />

                {/* Data points */}
                {radarDimensions.map((dim, i) => {
                  const p = getPoint(dim.value, dim.angle);
                  return (
                    <circle
                      key={i}
                      cx={p.x}
                      cy={p.y}
                      r="4"
                      fill="#BDA9FF"
                      fillOpacity="0.8"
                    />
                  );
                })}

                {/* Definitions */}
                <defs>
                  <linearGradient id="radarGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" stopColor="#BDA9FF" />
                    <stop offset="100%" stopColor="#C5A8FF" />
                  </linearGradient>
                  <filter id="radarGlow">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                    <feMerge>
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
              </svg>
            </div>

            {/* Legend */}
            <div className="flex-1 pt-2 min-w-0">
              <div className="space-y-2.5">
                {radarDimensions.map((dim, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div
                      className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                      style={{ background: "#BDA9FF", opacity: dim.value / 100 }}
                    />
                    <span className="text-[12px] text-echo-text/60 whitespace-nowrap">{dim.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* AI summary */}
          <div className="mt-2 p-4 rounded-2xl bg-echo-lavender/10">
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles size={12} strokeWidth={2} className="text-echo-lavender" />
              <span className="text-[11px] font-medium text-echo-text/50">Echo 观察</span>
            </div>
            <p className="text-echo-text/70 text-[13px] leading-relaxed">
              最近的你，更愿意表达真实感受，也开始学会坚定地表达自己的边界。
            </p>
          </div>
        </div>
      </section>

      {/* AI Dependency Trend */}
      <section className="px-5 pb-5">
        <h2 className="text-[17px] font-bold text-echo-text mb-3">🌿 越来越相信自己的表达</h2>
        <div className="glass-card rounded-card p-6">
          <div className="flex flex-col items-center">
            <div className="relative w-32 h-32 mb-4">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 100 100">
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke="rgba(234, 244, 255, 0.3)"
                  strokeWidth="8"
                />
                <circle
                  cx="50"
                  cy="50"
                  r="42"
                  fill="none"
                  stroke="url(#glowGradient)"
                  strokeWidth="8"
                  strokeLinecap="round"
                  strokeDasharray={`${83 * 2.64} 264`}
                  filter="url(#glowFilter)"
                />
                <defs>
                  <linearGradient id="glowGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#6EE7B7" />
                    <stop offset="50%" stopColor="#C5A8FF" />
                    <stop offset="100%" stopColor="#6EE7B7" />
                  </linearGradient>
                  <filter id="glowFilter">
                    <feGaussianBlur stdDeviation="2" result="coloredBlur" />
                    <feMerge>
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="coloredBlur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-[28px] font-bold text-echo-text" style={{ fontFamily: '"SF Pro Display", system-ui' }}>83%</span>
              </div>
            </div>
            <p className="text-echo-text/60 text-[13px] text-center leading-relaxed mb-3">
              本周已有 <span className="text-echo-text font-medium">83%</span> 的练习，
              <br />都是你先完成第一反应后，
              <br />才查看 Echo 的建议。
            </p>
            <p className="text-[#8B7355] text-[12px] italic">
              Echo 希望自己出现得越来越少。
            </p>
          </div>
        </div>
      </section>

      {/* Growth Timeline */}
      <section className="px-5 pb-5">
        <h2 className="text-[17px] font-bold text-echo-text mb-3">📈 我的成长轨迹</h2>
        <div className="glass-card rounded-card p-5">
          <div className="relative">
            {growthTimeline.map((item, i) => (
              <div key={i} className="flex gap-3 mb-4 last:mb-0">
                <div className="flex flex-col items-center">
                  <div className="w-9 h-9 rounded-full bg-echo-lavender/20 flex items-center justify-center">
                    <span className="text-[16px]">{item.emoji}</span>
                  </div>
                  {i < growthTimeline.length - 1 && (
                    <div className="w-0.5 h-6 bg-echo-lavender/20 mt-1" />
                  )}
                </div>
                <div className="flex-1 pt-1.5">
                  <p className="text-echo-text/75 text-[13px] leading-relaxed">{item.title}</p>
                  <p className="text-echo-text/35 text-[11px] mt-0.5">{item.date}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Echo Insight */}
      <section className="px-5 pb-5">
        <h2 className="text-[17px] font-bold text-echo-text mb-3">🪞 Echo 洞察</h2>
        <div className="glass-card rounded-card p-5">
          <div className="flex items-center gap-2 mb-4">
            <Brain size={16} strokeWidth={2} className="text-echo-lavender" />
            <span className="text-[13px] font-medium text-echo-text/60">本周观察</span>
          </div>
          <div className="space-y-3">
            {echoInsights.map((insight, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <span className="text-echo-lavender text-[12px] mt-0.5">●</span>
                <p className="text-echo-text/65 text-[13px] leading-relaxed">{insight}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expression Garden */}
      <section className="px-5 pb-5">
        <h2 className="text-[17px] font-bold text-echo-text mb-3">🌸 我的表达花园</h2>
        <div className="glass-card rounded-card p-5">
          <p className="text-echo-text/45 text-[12px] mb-4">
            每成功内化一条表达，就会长出一朵新的花。
          </p>

          {/* Garden */}
          <div className="relative h-48 mb-4 overflow-hidden rounded-2xl" style={{ 
            background: `
              radial-gradient(ellipse at 20% 10%, rgba(234,244,255,0.25) 0%, transparent 50%),
              radial-gradient(ellipse at 80% 20%, rgba(221,246,232,0.2) 0%, transparent 50%),
              radial-gradient(ellipse at 50% 80%, rgba(205,234,248,0.15) 0%, transparent 50%),
              linear-gradient(180deg, #F8FAFB 0%, #EAF4FF 50%, #DDF6E8 100%)
            ` 
          }}>
            {/* Ground */}
            <div className="absolute bottom-0 left-0 right-0 h-12" style={{ background: "linear-gradient(180deg, transparent, rgba(221,248,242,0.3))" }} />

            {/* Flowers */}
            <div className="absolute inset-0 flex items-end justify-around pb-3 px-2">
              {expressionGarden.map((flower, i) => (
                <div
                  key={i}
                  className={`flex flex-col items-center cursor-pointer transition-all duration-300 ${
                    selectedFlower === i ? "scale-125 -translate-y-2" : "hover:scale-110"
                  }`}
                  style={{ animationDelay: `${i * 0.15}s` }}
                  onClick={() => setSelectedFlower(selectedFlower === i ? null : i)}
                >
                  <span
                    className="text-[24px] mb-1"
                    style={{
                      filter: `drop-shadow(0 2px 6px ${flower.color}40)`,
                    }}
                  >
                    {flower.emoji}
                  </span>
                  <span className="text-[9px] text-echo-text/40">{flower.count}朵</span>
                </div>
              ))}
            </div>

            {/* Glow orbs */}
            <div className="glow-orb" style={{ width: 60, height: 60, background: "#EAF4FF", top: 10, left: 20, opacity: 0.25, filter: "blur(20px)" }} />
            <div className="glow-orb" style={{ width: 50, height: 50, background: "#DDF6E8", top: 30, right: 15, opacity: 0.2, filter: "blur(15px)" }} />
          </div>

          {/* Selected flower info */}
          {selectedFlower !== null && (
            <div className="p-3 rounded-2xl bg-[#EAF4FF]/20 animate-fade-slide">
              <div className="flex items-center gap-2 mb-1.5">
                <span className="text-[18px]">{expressionGarden[selectedFlower].emoji}</span>
                <span className="text-[13px] font-medium text-echo-text/70">
                  {expressionGarden[selectedFlower].type}
                </span>
                <span className="text-[11px] text-echo-text/40 ml-auto">
                  {expressionGarden[selectedFlower].count} 条已内化
                </span>
              </div>
              <p className="text-echo-text/50 text-[12px]">
                点击查看所有 {expressionGarden[selectedFlower].type} 表达
              </p>
            </div>
          )}

          {/* Type legend */}
          <div className="flex flex-wrap gap-2">
            {expressionGarden.slice(0, 3).map((flower, i) => (
              <div key={i} className="flex items-center gap-1 px-2 py-1 rounded-full bg-white/30">
                <span className="text-[12px]">{flower.emoji}</span>
                <span className="text-[10px] text-echo-text/50">{flower.type}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Expression Assets */}
      <section className="px-5 pb-5">
        <h2 className="text-[17px] font-bold text-echo-text mb-3">📚 我的表达资产</h2>
        <div className="glass-card rounded-card p-5">
          <div className="grid grid-cols-2 gap-3">
            {[
              { value: "128", label: "累计收藏", icon: "📚" },
              { value: "23", label: "成功化用", icon: "✨" },
              { value: "15", label: "完成排练", icon: "🎭" },
              { value: "7", label: "完成复盘", icon: "🪞" },
            ].map((stat, i) => (
              <div key={i} className="text-center p-3 rounded-2xl bg-white/25">
                <span className="text-[18px]">{stat.icon}</span>
                <p className="text-[22px] font-bold text-echo-text mt-1" style={{ fontFamily: '"SF Pro Display", system-ui' }}>
                  {stat.value}
                </p>
                <p className="text-[11px] text-echo-text/40 mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
          <div className="mt-3 flex items-center justify-center gap-1.5">
            <span className="text-[11px] text-echo-lavender">+ 5</span>
            <span className="text-[11px] text-echo-text/40">最近新增表达</span>
          </div>
        </div>
      </section>

      {/* Weekly Favorite Quote */}
      <section className="px-5 pb-5">
        <h2 className="text-[17px] font-bold text-echo-text mb-3">✨ 本周最喜欢的一句话</h2>
        <div className="glass-card rounded-card p-6 text-center">
          <p className="text-echo-text text-[16px] leading-relaxed font-medium mb-3">
            "{weeklyQuote.content}"
          </p>
          <p className="text-echo-text/40 text-[11px] mb-4">{weeklyQuote.source}</p>

          <div className="p-3 rounded-2xl bg-echo-lavender/10">
            <div className="flex items-center justify-center gap-1.5 mb-1.5">
              <Sparkles size={11} strokeWidth={2} className="text-echo-lavender" />
              <span className="text-[11px] font-medium text-echo-text/50">Echo Insight</span>
            </div>
            <p className="text-echo-text/60 text-[12px] leading-relaxed">
              {weeklyQuote.insight}
            </p>
          </div>
        </div>
      </section>

      {/* Letter from Echo */}
      <section className="px-5 pb-5">
        <h2 className="text-[17px] font-bold text-echo-text mb-3">💌 来自 Echo 的一封信</h2>
        <div
          className="glass-card rounded-card p-6 relative overflow-hidden"
          style={{ background: "linear-gradient(135deg, rgba(255,217,242,0.25) 0%, rgba(189,169,255,0.2) 100%)" }}
        >
          {/* Decorative corner */}
          <div className="absolute top-3 right-4 text-[20px] opacity-20">💌</div>

          <div className="relative z-10 space-y-1.5">
            {echoLetter.map((line, i) => (
              <p
                key={i}
                className={`text-echo-text/70 text-[13px] leading-relaxed ${
                  line === "" ? "h-2" : ""
                }`}
              >
                {line}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Closing Card */}
      <section className="px-5 pb-5">
        <div 
          className="rounded-card p-8 text-center relative overflow-hidden"
          style={{
            background: `
              radial-gradient(ellipse at 20% 30%, rgba(168, 230, 207, 0.25) 0%, transparent 50%),
              radial-gradient(ellipse at 80% 70%, rgba(197, 168, 255, 0.2) 0%, transparent 50%),
              radial-gradient(ellipse at 50% 50%, rgba(234, 244, 255, 0.3) 0%, transparent 60%),
              rgba(255,255,255,0.5)
            `,
            backdropFilter: "blur(24px)",
            border: "1px solid rgba(255,255,255,0.5)",
            boxShadow: "0 12px 40px rgba(80,90,140,0.08)",
          }}
        >
          <h2 className="text-[18px] font-bold text-echo-text mb-4">
            成长，不是学会更多漂亮的话。
          </h2>
          <p className="text-echo-text/60 text-[14px] leading-relaxed mb-6">
            而是在下一次真实的人生对话里，
            <br />你终于能够说出，
            <br />那句真正属于自己的话。
          </p>
          <p className="text-echo-text/25 text-[12px] italic">
            谢谢你愿意慢慢练习。
          </p>
        </div>
      </section>

      {/* Settings entry */}
      <section className="px-5 pb-8">
        <div className="glass-card rounded-card p-4">
          {[
            { icon: "👤", label: "账号设置" },
            { icon: "🔔", label: "通知设置" },
            { icon: "🎨", label: "主题与外观" },
            { icon: "🔒", label: "隐私与安全" },
          ].map((item, i) => (
            <div
              key={i}
              className={`flex items-center gap-3 py-3 ${
                i < 3 ? "border-b border-echo-text/5" : ""
              }`}
            >
              <span className="text-[18px]">{item.icon}</span>
              <span className="text-echo-text/70 text-[14px] flex-1">{item.label}</span>
              <ChevronRight size={16} strokeWidth={2} className="text-echo-text/30" />
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
